import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { usePatientMetadata } from '../../providers/PatientMetadataProvider';
import PatientRecapCard from '../PatientRecapCard';
import Spinner from '../Spinner';
import { ClarificationHistory } from '../clarifications/ClarificationHistory';

interface RespondToQuestionModalProps {
    isOpen: boolean;
    patientId: string | null;
    patientName: string;
    statusLabel: string;
    isSubmitting: boolean;
    onClose: () => void;
    onSubmit: (response: string) => void;
}

/**
 * Respond to ODO's clarification ask while the case is in Hold-5. Layout
 * matches UploadRecordsModal / ReviewRefinementModal:
 *
 *   PatientRecapCard
 *   Clarifications (shared ClarificationHistory component — fetches its own data)
 *   Your response (textarea)
 *   Footer: [Cancel] [Send response]
 */
const RespondToQuestionModal: React.FC<RespondToQuestionModalProps> = ({
    isOpen,
    patientId,
    patientName,
    statusLabel,
    isSubmitting,
    onClose,
    onSubmit
}) => {
    const { getProfilePictureUrl, getProfileThumbnailUrl } = usePatientMetadata();
    const [response, setResponse] = useState('');

    useEffect(() => {
        if (!isOpen) setResponse('');
    }, [isOpen]);

    if (!isOpen || !patientId) return null;

    const trimmed = response.trim();
    const canSubmit = trimmed.length > 0 && !isSubmitting;

    const profilePictureUrl = getProfilePictureUrl(patientId);
    const profileThumbnailUrl = getProfileThumbnailUrl(patientId);
    const handleBackdropClick = isSubmitting ? undefined : onClose;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={handleBackdropClick}
        >
            <div
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4">
                    <h2 className="text-xl font-bold text-gray-800">Respond to question</h2>
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className={`${isSubmitting ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-800'}`}
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 min-h-0 px-6 py-4 space-y-5 overflow-y-auto scrollbar-hide">
                    <PatientRecapCard
                        name={patientName}
                        profilePictureUrl={profilePictureUrl || ''}
                        profileThumbnailUrl={profileThumbnailUrl}
                        treatmentStatus={statusLabel}
                        duration="N/A"
                        currentAligner={0}
                        totalAligners={0}
                    />

                    <ClarificationHistory
                        patientId={patientId}
                        title="Clinical question"
                        latestQuestionOnly
                    />

                    {/* Response */}
                    <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-800">
                            Your response<span className="text-red-500">*</span>
                        </p>
                        <textarea
                            value={response}
                            onChange={(e) => setResponse(e.target.value)}
                            placeholder="Provide your response here..."
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl placeholder-gray-400/60 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 transition-colors duration-200 resize-none h-36 disabled:bg-gray-50 disabled:cursor-not-allowed"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg border ${
                            isSubmitting
                                ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                                : 'text-gray-800 bg-white border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSubmit(trimmed)}
                        disabled={!canSubmit}
                        className={`px-6 py-2.5 text-sm font-bold rounded-lg flex items-center gap-2 ${
                            canSubmit
                                ? 'bg-gray-800 text-white hover:bg-gray-700'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {isSubmitting && <Spinner />}
                        Send response
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RespondToQuestionModal;
